import { useMemo, type ContextType, type ReactNode, type Ref } from 'react'

import { mapToWidgetConfig } from '@/config/utils/map-widget-config'
import type { WidgetConfigDto } from '@/config/types/widget-config-dto'
import { WidgetConfigContext } from '@/contexts/widget-config-context'
import { WidgetNavigationContext } from '@/contexts/widget-navigation-context'
import { TemplateRenderer } from '@/features/conversation/components/conversation-message/ai-assistant-message/widget-template/template-renderer'
import { CardActionContext } from '@/features/conversation/contexts/card-action-context'
import type { WidgetTemplate } from '@/features/conversation/types/message'

/**
 * One template card drawn by the widget's own renderer, with no widget around it and
 * no conversation behind it. The dashboard's catalog preview (/sdk/?card), the render
 * API's page and the templates render SDK all draw a card through this.
 */

const noop = () => {}

const NAVIGATION: NonNullable<ContextType<typeof WidgetNavigationContext>> = {
  currentScreen: 'conversation',
  conversationId: null,
  navigationDirection: 'forward',
  initialMessage: null,
  navigateToIntro: noop,
  navigateToWelcome: noop,
  navigateToPreChat: noop,
  navigateToConversation: noop,
  setInitialMessage: noop,
  goBack: noop,
}

export function cardLang(template: WidgetTemplate | null | undefined): 'ar' | 'en' {
  return (template as { content?: { lang?: string } } | undefined)?.content?.lang === 'en' ? 'en' : 'ar'
}

export function CardView({
  template,
  theme,
  boxRef,
  padding = 12,
  onAction,
}: {
  template: WidgetTemplate | null
  theme?: 'light' | 'dark'
  boxRef?: Ref<HTMLDivElement>
  padding?: number
  onAction?: ContextType<typeof CardActionContext>
}) {
  return (
    <WidgetSurface lang={cardLang(template)} theme={theme} boxRef={boxRef} padding={padding} onAction={onAction}>
      {template && <TemplateRenderer widgetTemplate={template} sourceMessageUlid="preview" />}
    </WidgetSurface>
  )
}

/**
 * What the widget's renderers need around them, with no widget and no conversation: a
 * config with every section off, a navigation that goes nowhere, the language's direction
 * and the theme's background. A card (CardView) and a whole message (the render SDK's
 * message view) are both drawn inside it.
 */
export function WidgetSurface({
  lang,
  theme,
  boxRef,
  padding = 12,
  children,
  onAction,
}: {
  lang: 'ar' | 'en'
  theme?: 'light' | 'dark'
  boxRef?: Ref<HTMLDivElement>
  padding?: number
  children: ReactNode
  /** Where the cards' buttons go when the page drawing them handles them (the render SDK); none = they do nothing. */
  onAction?: ContextType<typeof CardActionContext>
}) {
  const dark = theme === 'dark'
  const value = useMemo(
    () => ({
      widgetConfig: mapToWidgetConfig({
        name: 'Preview',
        locale: lang,
        // Every section the mapper reads, switched off: only the card matters here.
        widget_v2_config: {
          intro_screen: {
            showIntroScreen: { value: false },
            widgetBackground: { type: 'color', background: dark ? '#111111' : '#FFFFFF' },
          },
          quick_links: { showQuickLinks: { value: false }, quickLinkCards: [], displaySettings: {} },
          trending_prompts: { showTrendingPrompts: { value: false }, promptChips: [], displaySettings: {} },
          chat_input: { inputActions: {}, placeholderText: { text: '' } },
          messages: { feedback: { showFeedbackButtons: false }, aiMessages: {}, customerMessages: {} },
        },
      } as unknown as WidgetConfigDto),
      view: { lang, theme: dark ? 'dark' : 'light' } as never,
      setView: noop,
    }),
    [lang, dark],
  )

  // with a handler, the cards' buttons have somewhere to go: a stand-in conversation lets them fire
  const navigation = useMemo(() => (onAction ? { ...NAVIGATION, conversationId: 'preview' } : NAVIGATION), [onAction])
  return (
    <WidgetConfigContext.Provider value={value}>
      <CardActionContext.Provider value={onAction ?? null}>
      <WidgetNavigationContext.Provider value={navigation}>
        <div
          ref={boxRef}
          dir={lang === 'ar' ? 'rtl' : 'ltr'}
          className={dark ? 'dark' : undefined}
          style={{ padding, background: dark ? '#111111' : '#FFFFFF', fontFamily: "'DM Sans', system-ui, sans-serif" }}
        >
          {children}
        </div>
      </WidgetNavigationContext.Provider>
      </CardActionContext.Provider>
    </WidgetConfigContext.Provider>
  )
}
